/**
 * Department aggregates -> projection save inputs.
 *
 * Upserts, into the inputs being saved, the department-level values computed by
 * SeasonalityModeView (exactly what the page displays): UNITS, TOTAL_EXPENSES,
 * OPERATING_PROFIT, the department gross, and OTHERS per-account income.
 *
 * - Zeros are persisted: skipping a zero left the previous aggregate stored, so a
 *   revision to 0 never reached DynamoDB / the Forecast page.
 * - Vehicle departments: the aggregate gross is Front + F&I, so it is saved as
 *   TOTAL_GROSS. Writing it as GROSS overwrote the front-only value that the
 *   consolidated loader reads as front gross.
 *
 * Extracted from Projections.tsx handleSaveInternal (mutates `inputsWithSeasonality`).
 */

import { MonthlyTotals, createEmptyMonthlyTotals } from '../types';
import { ProjectionInput } from '../types/projections';
import type { DepartmentAggregates } from '../components/projections';

/** Departments whose aggregate gross is Front + F&I (saved as TOTAL_GROSS) */
export const VEHICLE_DEPARTMENTS = ['NEW_VEHICLE', 'USED_VEHICLE'];

export function applyDepartmentAggregates(
  inputsWithSeasonality: ProjectionInput[],
  departmentAggregates: DepartmentAggregates[],
  seasonalityYears: number[]
): void {
  // Update or add aggregate UNITS inputs for NEW_VEHICLE and USED_VEHICLE
  // Use the pre-calculated values from SeasonalityModeView - these are the EXACT values displayed
  // so the Forecast page can just read them directly - no backend calculation needed
  for (const agg of departmentAggregates) {
    // Only vehicle departments have units
    if (agg.department !== 'NEW_VEHICLE' && agg.department !== 'USED_VEHICLE') continue;
    // Persist 0 too, so a revision to zero replaces the previous UNITS total
    if (agg.units === undefined || !agg.unitsMonthly) continue;

    // Calculate seasonality weights from the monthly distribution
    const unitsWeights = createEmptyMonthlyTotals();
    if (agg.units !== 0) {
      for (let m = 1; m <= 12; m++) {
        const key = `month${m}` as keyof MonthlyTotals;
        unitsWeights[key] = agg.unitsMonthly[key] / agg.units;
      }
    }

    const newUnitsData = {
      department: agg.department,
      metric: 'UNITS',
      inputType: 'amount' as const,
      value: Math.round(agg.units),
      monthlyDistribution: {
        month1: Math.round(agg.unitsMonthly.month1),
        month2: Math.round(agg.unitsMonthly.month2),
        month3: Math.round(agg.unitsMonthly.month3),
        month4: Math.round(agg.unitsMonthly.month4),
        month5: Math.round(agg.unitsMonthly.month5),
        month6: Math.round(agg.unitsMonthly.month6),
        month7: Math.round(agg.unitsMonthly.month7),
        month8: Math.round(agg.unitsMonthly.month8),
        month9: Math.round(agg.unitsMonthly.month9),
        month10: Math.round(agg.unitsMonthly.month10),
        month11: Math.round(agg.unitsMonthly.month11),
        month12: Math.round(agg.unitsMonthly.month12),
      },
      seasonalityWeights: unitsWeights,
      yearsUsedForWeights: [...seasonalityYears],
    };

    // Find and UPDATE existing UNITS input, or add new one
    const existingIndex = inputsWithSeasonality.findIndex(
      i => i.department === agg.department && i.metric === 'UNITS'
    );
    if (existingIndex >= 0) {
      // Replace existing with displayed values
      inputsWithSeasonality[existingIndex] = newUnitsData;
      console.log(`Updated ${agg.department}#UNITS: ${Math.round(agg.units)} annual, Jan=${Math.round(agg.unitsMonthly.month1)}`);
    } else {
      // Add new
      inputsWithSeasonality.push(newUnitsData);
      console.log(`Added ${agg.department}#UNITS: ${Math.round(agg.units)} annual, Jan=${Math.round(agg.unitsMonthly.month1)}`);
    }
  }

  // Update or add TOTAL_EXPENSES and OPERATING_PROFIT for each department
  // Use the pre-calculated values from SeasonalityModeView - these are the EXACT values displayed
  // This ensures the backend matches exactly what the user sees on screen
  for (const agg of departmentAggregates) {
    // Always persisted (zeros included): skipping a zero left the previous
    // TOTAL_EXPENSES / OPERATING_PROFIT aggregate stored and shown on Forecast

    // Calculate seasonality weights from the monthly distribution
    const totalExpensesWeights = createEmptyMonthlyTotals();
    if (agg.totalExpenses !== 0) {
      for (let m = 1; m <= 12; m++) {
        const key = `month${m}` as keyof MonthlyTotals;
        totalExpensesWeights[key] = agg.totalExpensesMonthly[key] / agg.totalExpenses;
      }
    }

    const newExpensesData = {
      department: agg.department,
      metric: 'TOTAL_EXPENSES',
      inputType: 'amount' as const,
      value: Math.round(agg.totalExpenses),
      monthlyDistribution: {
        month1: Math.round(agg.totalExpensesMonthly.month1),
        month2: Math.round(agg.totalExpensesMonthly.month2),
        month3: Math.round(agg.totalExpensesMonthly.month3),
        month4: Math.round(agg.totalExpensesMonthly.month4),
        month5: Math.round(agg.totalExpensesMonthly.month5),
        month6: Math.round(agg.totalExpensesMonthly.month6),
        month7: Math.round(agg.totalExpensesMonthly.month7),
        month8: Math.round(agg.totalExpensesMonthly.month8),
        month9: Math.round(agg.totalExpensesMonthly.month9),
        month10: Math.round(agg.totalExpensesMonthly.month10),
        month11: Math.round(agg.totalExpensesMonthly.month11),
        month12: Math.round(agg.totalExpensesMonthly.month12),
      },
      seasonalityWeights: totalExpensesWeights,
      yearsUsedForWeights: [...seasonalityYears],
    };

    // Find and UPDATE existing TOTAL_EXPENSES, or add new one
    const existingExpensesIndex = inputsWithSeasonality.findIndex(
      i => i.department === agg.department && i.metric === 'TOTAL_EXPENSES'
    );
    if (existingExpensesIndex >= 0) {
      inputsWithSeasonality[existingExpensesIndex] = newExpensesData;
      console.log(`Updated ${agg.department}#TOTAL_EXPENSES: ${Math.round(agg.totalExpenses)} annual, Jan=${Math.round(agg.totalExpensesMonthly.month1)}`);
    } else {
      inputsWithSeasonality.push(newExpensesData);
      console.log(`Added ${agg.department}#TOTAL_EXPENSES: ${Math.round(agg.totalExpenses)} annual, Jan=${Math.round(agg.totalExpensesMonthly.month1)}`);
    }

    // Calculate seasonality weights for operating profit
    const operatingProfitWeights = createEmptyMonthlyTotals();
    const absProfit = Math.abs(agg.operatingProfit);
    if (absProfit > 0) {
      for (let m = 1; m <= 12; m++) {
        const key = `month${m}` as keyof MonthlyTotals;
        operatingProfitWeights[key] = agg.operatingProfitMonthly[key] / agg.operatingProfit;
      }
    }

    const newProfitData = {
      department: agg.department,
      metric: 'OPERATING_PROFIT',
      inputType: 'amount' as const,
      value: Math.round(agg.operatingProfit),
      monthlyDistribution: {
        month1: Math.round(agg.operatingProfitMonthly.month1),
        month2: Math.round(agg.operatingProfitMonthly.month2),
        month3: Math.round(agg.operatingProfitMonthly.month3),
        month4: Math.round(agg.operatingProfitMonthly.month4),
        month5: Math.round(agg.operatingProfitMonthly.month5),
        month6: Math.round(agg.operatingProfitMonthly.month6),
        month7: Math.round(agg.operatingProfitMonthly.month7),
        month8: Math.round(agg.operatingProfitMonthly.month8),
        month9: Math.round(agg.operatingProfitMonthly.month9),
        month10: Math.round(agg.operatingProfitMonthly.month10),
        month11: Math.round(agg.operatingProfitMonthly.month11),
        month12: Math.round(agg.operatingProfitMonthly.month12),
      },
      seasonalityWeights: operatingProfitWeights,
      yearsUsedForWeights: [...seasonalityYears],
    };

    // Find and UPDATE existing OPERATING_PROFIT, or add new one
    const existingProfitIndex = inputsWithSeasonality.findIndex(
      i => i.department === agg.department && i.metric === 'OPERATING_PROFIT'
    );
    if (existingProfitIndex >= 0) {
      inputsWithSeasonality[existingProfitIndex] = newProfitData;
      console.log(`Updated ${agg.department}#OPERATING_PROFIT: ${Math.round(agg.operatingProfit)} annual, Jan=${Math.round(agg.operatingProfitMonthly.month1)}`);
    } else {
      inputsWithSeasonality.push(newProfitData);
      console.log(`Added ${agg.department}#OPERATING_PROFIT: ${Math.round(agg.operatingProfit)} annual, Jan=${Math.round(agg.operatingProfitMonthly.month1)}`);
    }

    // Also save the department gross (needed for Forecast page projection line).
    // Vehicle departments: agg.gross is Front + F&I, so it is saved as TOTAL_GROSS -
    // writing it as GROSS overwrote the front-only value that consolidation reads.
    // Zeros are persisted too.
    {
      const grossMetric = VEHICLE_DEPARTMENTS.includes(agg.department) ? 'TOTAL_GROSS' : 'GROSS';
      const grossWeights = createEmptyMonthlyTotals();
      if (agg.gross !== 0) {
        for (let m = 1; m <= 12; m++) {
          const key = `month${m}` as keyof MonthlyTotals;
          grossWeights[key] = agg.grossMonthly[key] / agg.gross;
        }
      }

      const newGrossData = {
        department: agg.department,
        metric: grossMetric,
        inputType: 'amount' as const,
        value: Math.round(agg.gross),
        monthlyDistribution: {
          month1: Math.round(agg.grossMonthly.month1),
          month2: Math.round(agg.grossMonthly.month2),
          month3: Math.round(agg.grossMonthly.month3),
          month4: Math.round(agg.grossMonthly.month4),
          month5: Math.round(agg.grossMonthly.month5),
          month6: Math.round(agg.grossMonthly.month6),
          month7: Math.round(agg.grossMonthly.month7),
          month8: Math.round(agg.grossMonthly.month8),
          month9: Math.round(agg.grossMonthly.month9),
          month10: Math.round(agg.grossMonthly.month10),
          month11: Math.round(agg.grossMonthly.month11),
          month12: Math.round(agg.grossMonthly.month12),
        },
        seasonalityWeights: grossWeights,
        yearsUsedForWeights: [...seasonalityYears],
      };

      const existingGrossIndex = inputsWithSeasonality.findIndex(
        i => i.department === agg.department && i.metric === grossMetric
      );
      if (existingGrossIndex >= 0) {
        inputsWithSeasonality[existingGrossIndex] = newGrossData;
        console.log(`Updated ${agg.department}#${grossMetric}: ${Math.round(agg.gross)} annual, Jan=${Math.round(agg.grossMonthly.month1)}`);
      } else {
        inputsWithSeasonality.push(newGrossData);
        console.log(`Added ${agg.department}#${grossMetric}: ${Math.round(agg.gross)} annual, Jan=${Math.round(agg.grossMonthly.month1)}`);
      }
    }

    // For OTHERS department, save per-account income totals (e.g., INCOME_850)
    // This enables consolidated aggregation of per-account values
    if (agg.department === 'OTHERS' && agg.incomeAccounts) {
      for (const [accCode, amounts] of Object.entries(agg.incomeAccounts)) {
        // Zero is persisted too (a revision to 0 must replace the old total)
        const incomeWeights = createEmptyMonthlyTotals();
        if (amounts.annual !== 0) {
          for (let m = 1; m <= 12; m++) {
            const key = `month${m}` as keyof MonthlyTotals;
            incomeWeights[key] = amounts.monthly[key] / amounts.annual;
          }
        }

        const newIncomeData = {
          department: agg.department,
          metric: `INCOME_${accCode}`,
          inputType: 'amount' as const,
          value: Math.round(amounts.annual),
          monthlyDistribution: {
            month1: Math.round(amounts.monthly.month1),
            month2: Math.round(amounts.monthly.month2),
            month3: Math.round(amounts.monthly.month3),
            month4: Math.round(amounts.monthly.month4),
            month5: Math.round(amounts.monthly.month5),
            month6: Math.round(amounts.monthly.month6),
            month7: Math.round(amounts.monthly.month7),
            month8: Math.round(amounts.monthly.month8),
            month9: Math.round(amounts.monthly.month9),
            month10: Math.round(amounts.monthly.month10),
            month11: Math.round(amounts.monthly.month11),
            month12: Math.round(amounts.monthly.month12),
          },
          seasonalityWeights: incomeWeights,
          yearsUsedForWeights: [...seasonalityYears],
        };

        const existingIncomeIndex = inputsWithSeasonality.findIndex(
          i => i.department === agg.department && i.metric === `INCOME_${accCode}`
        );
        if (existingIncomeIndex >= 0) {
          inputsWithSeasonality[existingIncomeIndex] = newIncomeData;
        } else {
          inputsWithSeasonality.push(newIncomeData);
        }
        console.log(`Added ${agg.department}#INCOME_${accCode}: ${Math.round(amounts.annual)} annual`);
      }
    }
  }
}
